"use client"

import * as React from 'react'
import Link from 'next/link'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { Button } from '@open-mercato/ui/primitives/button'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type CargoListItem = {
  id: string
  cargoType: string
  referenceNumber: string
  containerNumber?: string | null
  palletCount?: number | null
  bulkMaterial?: string | null
  currentMode?: string | null
  lastEventAt?: string | null
  currentLocation?: {
    label?: string | null
    latitude?: number | null
    longitude?: number | null
  } | null
  assignments?: Array<{ id: string; salesShipmentId?: string | null }>
}

const formatLocation = (location: CargoListItem['currentLocation']) => {
  if (!location) return null
  if (location.label) return location.label
  if (location.latitude !== null && location.latitude !== undefined && location.longitude !== null && location.longitude !== undefined) {
    return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`
  }
  return null
}

export default function CargoListPage() {
  const t = useT()
  const [items, setItems] = React.useState<CargoListItem[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let mounted = true
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await apiCall<{ items?: CargoListItem[] }>('/api/freight/cargo?pageSize=100')
        if (!response.ok) throw new Error(t('cargo.pages.list.loadError', 'Failed to load cargo.'))
        if (!mounted) return
        setItems(Array.isArray(response.result?.items) ? response.result.items : [])
      } catch (loadError) {
        if (!mounted) return
        setError(loadError instanceof Error ? loadError.message : t('cargo.pages.list.loadError', 'Failed to load cargo.'))
      } finally {
        if (mounted) setLoading(false)
      }
    }
    void load()
    return () => {
      mounted = false
    }
  }, [t])

  return (
    <Page>
      <PageBody>
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold">{t('cargo.pages.list.title', 'Cargo')}</h1>
              <p className="text-sm text-muted-foreground">
                {t('cargo.pages.list.description', 'Track registered freight units and their current operational state.')}
              </p>
            </div>
            <Button asChild>
              <Link href="/backend/cargo/create">
                {t('cargo.pages.create.action', 'Register cargo')}
              </Link>
            </Button>
          </div>

          {loading ? <LoadingMessage label={t('cargo.pages.list.loading', 'Loading cargo…')} /> : null}
          {error ? <ErrorMessage label={error} /> : null}

          {!loading && !error && items.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-sm text-muted-foreground">
              {t('cargo.pages.list.empty', 'No cargo has been registered yet.')}
            </div>
          ) : null}

          {!loading && !error && items.length > 0 ? (
            <div className="grid gap-4">
              {items.map((item) => (
                <Link
                  key={item.id}
                  href={`/backend/cargo/${item.id}`}
                  className="rounded-xl border bg-card p-4 transition-colors hover:border-foreground/30"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="space-y-1">
                      <div className="text-base font-semibold">{item.referenceNumber}</div>
                      <div className="text-sm text-muted-foreground">
                        {t(`cargo.types.${item.cargoType}`, item.cargoType)}
                      </div>
                    </div>
                    <div className="text-right text-sm text-muted-foreground">
                      <div>{item.currentMode ? t(`cargo.modes.${item.currentMode}`, item.currentMode) : t('cargo.state.modeUnknown', 'Mode unknown')}</div>
                      <div>{item.lastEventAt ? new Date(item.lastEventAt).toLocaleString() : t('cargo.state.noEvents', 'No events yet')}</div>
                    </div>
                  </div>
                  <div className="mt-4 grid gap-2 text-sm text-muted-foreground md:grid-cols-3">
                    <div>
                      {item.containerNumber ??
                        (item.palletCount ? `${item.palletCount} ${t('cargo.labels.pallets', 'pallets')}` : item.bulkMaterial ?? t('cargo.labels.noSubtype', 'No subtype details'))}
                    </div>
                    <div>{formatLocation(item.currentLocation) ?? t('cargo.state.locationUnknown', 'Location unknown')}</div>
                    <div>
                      {item.assignments?.length
                        ? t('cargo.labels.assignmentCount', '{{count}} links', { count: item.assignments.length })
                        : t('cargo.labels.unlinked', 'Unlinked')}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      </PageBody>
    </Page>
  )
}
